import { useRouter } from "next/router";
import React, { createContext, useContext } from "react";

interface LanguageContextValue {
  appLang: string;
}

const LanguageDataContext = createContext<LanguageContextValue>({
  appLang: "id",
});

/**
 * LanguageProvider - Pure data provider for current locale
 *
 * IMPORTANT: This provider does NOT handle navigation.
 * Locale changes are handled by:
 * 1. Next.js i18n routing (SSR)
 * 2. Middleware rewrites (server-side)
 * 3. Explicit user actions (language switcher button)
 *
 * This prevents double document requests during hydration.
 */
const LanguageProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const router = useRouter();
  const { locale } = router;

  return (
    <LanguageDataContext.Provider
      value={{
        appLang: locale ?? "id",
      }}
    >
      {children}
    </LanguageDataContext.Provider>
  );
};

export default LanguageProvider;

/**
 * Hook to access current locale
 * @returns {LanguageContextValue} Current language context
 */
export const useLanguage = () => useContext(LanguageDataContext);
